import type { UniversalNodeSchema } from '../universal-node-schema'
import { UniversalNodeRegistry, createOHLCVInput } from '../universal-node-schema'
import { TrendingUp } from 'lucide-react'
import { bollingerName, macdName, movingAverageName, periodName } from '../node-labels'

/**
 * Technical Indicator Node Schemas for Universal Node System
 * This file defines all technical indicators using the universal schema pattern,
 * enabling a single component to render all technical indicators based on configuration.
 */

// Simple Moving Average (SMA)
const smaSchema: UniversalNodeSchema = {
  type: 'simplemovingaverage',
  face: { shortName: 'SMA', name: movingAverageName('SMA') },
  displayName: 'Simple Moving Average',
  aliases: ['SMA'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'sma',
      displayName: 'SMA',
      type: 'number',
      description: 'Sma output line',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 200,
    },
  ],
}

// Exponential Moving Average (EMA)
const emaSchema: UniversalNodeSchema = {
  type: 'exponentialmovingaverage',
  face: { shortName: 'EMA', name: movingAverageName('EMA') },
  displayName: 'Exponential Moving Average',
  aliases: ['EMA'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'ema',
      displayName: 'EMA',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 200,
    },
  ],
}

// Weighted Moving Average (WMA)
const wmaSchema: UniversalNodeSchema = {
  type: 'weightedmovingaverage',
  face: { shortName: 'WMA', name: movingAverageName('WMA') },
  displayName: 'Weighted Moving Average',
  aliases: ['WMA'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'wma',
      displayName: 'WMA',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 200,
    },
  ],
}

// Relative Strength Index (RSI)
const rsiSchema: UniversalNodeSchema = {
  type: 'relativestrengthindex',
  face: { shortName: 'RSI', name: periodName('RSI', 14) },
  displayName: 'Relative Strength Index',
  aliases: ['RSI'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'rsi',
      displayName: 'RSI',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
    },
  ],
}

// MACD
const macdSchema: UniversalNodeSchema = {
  type: 'macd',
  face: { shortName: 'MACD', name: macdName },
  displayName: 'MACD',
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'macd',
      displayName: 'MACD Line',
      type: 'number',
    },
    {
      id: 'signal',
      displayName: 'Signal Line',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period_me1',
      displayName: 'Fast',
      type: 'int',
      default: 12,
      min: 1,
      max: 100,
    },
    {
      name: 'period_me2',
      displayName: 'Slow',
      type: 'int',
      default: 26,
      min: 1,
      max: 100,
    },
    {
      name: 'period_signal',
      displayName: 'Signal',
      type: 'int',
      default: 9,
      min: 1,
      max: 100,
    },
  ],
}

// Bollinger Bands
const bollingerBandsSchema: UniversalNodeSchema = {
  type: 'bollingerbands',
  face: { shortName: 'BB', name: bollingerName },
  displayName: 'Bollinger Bands',
  aliases: ['BB', 'BBands'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'top',
      displayName: 'Top',
      type: 'number',
    },
    {
      id: 'mid',
      displayName: 'Middle',
      type: 'number',
    },
    {
      id: 'bot',
      displayName: 'Bottom',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 100,
    },
    {
      name: 'stdDev',
      displayName: 'Std Dev',
      type: 'float',
      default: 2.0,
      min: 0.1,
      max: 5.0,
    },
  ],
}

// Average True Range (ATR) - requires high, low, close
const atrSchema: UniversalNodeSchema = {
  type: 'averagetruerange',
  face: { shortName: 'ATR', name: periodName('ATR', 14) },
  displayName: 'Average True Range',
  aliases: ['ATR'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'atr',
      displayName: 'ATR',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
    },
  ],
}

// ADX (Average Directional Movement Index) - requires high, low, close
const adxSchema: UniversalNodeSchema = {
  type: 'averagedirectionalmovementindex',
  displayName: 'Average Directional Index',
  aliases: ['ADX', 'DMI'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'adx',
      displayName: 'ADX',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
    },
  ],
}

// Stochastic Oscillator - requires high, low, close
const stochasticSchema: UniversalNodeSchema = {
  type: 'stochasticfull',
  displayName: 'Stochastic Oscillator',
  aliases: ['STOCH', 'Stoch'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'percK',
      displayName: '%K Line',
      type: 'number',
    },
    {
      id: 'percD',
      displayName: '%D Line',
      type: 'number',
    },
    {
      id: 'percDSlow',
      displayName: '%D-Slow',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
    },
    {
      name: 'period_dfast',
      displayName: 'D Fast',
      type: 'int',
      default: 3,
      min: 1,
      max: 50,
    },
    {
      name: 'period_dslow',
      displayName: 'D Slow',
      type: 'int',
      default: 3,
      min: 1,
      max: 50,
    },
  ],
}

// Stochastic RSI
const stochasticRsiSchema: UniversalNodeSchema = {
  type: 'stochasticrsi',
  displayName: 'Stochastic RSI',
  aliases: ['StochRSI', 'SRSI'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'k',
      displayName: 'StochRSI %K',
      type: 'number',
    },
    {
      id: 'd',
      displayName: 'StochRSI %D',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'kPeriod',
      displayName: 'K Period',
      type: 'int',
      default: 3,
      min: 1,
      max: 50,
    },
    {
      name: 'kSmoothing',
      displayName: 'K Smoothing',
      type: 'int',
      default: 1,
      min: 1,
      max: 20,
    },
    {
      name: 'dPeriod',
      displayName: 'D Period',
      type: 'int',
      default: 3,
      min: 1,
      max: 50,
    },
  ],
}

// Williams %R - requires high, low, close
const williamsRSchema: UniversalNodeSchema = {
  type: 'williamsr',
  displayName: 'Williams %R',
  aliases: ['WPR', 'Williams'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'percR',
      displayName: '%R',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
    },
  ],
}

// Rate of Change (ROC)
const rocSchema: UniversalNodeSchema = {
  type: 'rateofchange',
  displayName: 'Rate of Change',
  aliases: ['ROC'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'roc',
      displayName: 'ROC',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 12,
      min: 1,
      max: 100,
    },
  ],
}

// Awesome Oscillator - requires high, low
const awesomeOscillatorSchema: UniversalNodeSchema = {
  type: 'awesomeoscillator',
  displayName: 'Awesome Oscillator',
  aliases: ['AO'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'ao',
      displayName: 'AO',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'fast',
      displayName: 'Fast',
      type: 'int',
      default: 5,
      min: 1,
      max: 50,
    },
    {
      name: 'slow',
      displayName: 'Slow',
      type: 'int',
      default: 34,
      min: 1,
      max: 100,
    },
  ],
}

// Know Sure Thing (KST)
const kstSchema: UniversalNodeSchema = {
  type: 'knowsurething',
  displayName: 'Know Sure Thing',
  aliases: ['KST'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'kst',
      displayName: 'KST',
      type: 'number',
    },
    {
      id: 'signal',
      displayName: 'Signal',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 10,
      min: 1,
      max: 100,
    },
  ],
}

// Parabolic SAR - requires high, low, close
const parabolicSarSchema: UniversalNodeSchema = {
  type: 'parabolicsar',
  displayName: 'Parabolic SAR',
  aliases: ['SAR', 'PSAR'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'psar',
      displayName: 'PSAR',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
    },
    {
      name: 'af',
      displayName: 'Increment',
      type: 'float',
      default: 0.02,
      min: 0.001,
      max: 1.0,
    },
    {
      name: 'afmax',
      displayName: 'Maximum',
      type: 'float',
      default: 0.2,
      min: 0.01,
      max: 1.0,
    },
  ],
}

// Keltner Channel - requires high, low, close
const keltnerChannelSchema: UniversalNodeSchema = {
  type: 'keltnerchannel',
  displayName: 'Keltner Channel',
  aliases: ['KC'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'upper',
      displayName: 'Upper Channel',
      type: 'number',
    },
    {
      id: 'middle',
      displayName: 'Middle Channel',
      type: 'number',
    },
    {
      id: 'lower',
      displayName: 'Lower Channel',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 100,
    },
    {
      name: 'mult',
      displayName: 'Multiplier',
      type: 'float',
      default: 2.0,
      min: 0.1,
      max: 10.0,
    },
    {
      name: 'source',
      displayName: 'Source',
      type: 'select',
      default: 'close',
      options: [
        { value: 'close', label: 'Close' },
        { value: 'ema', label: 'EMA' },
        { value: 'sma', label: 'SMA' },
        { value: 'wma', label: 'WMA' },
      ],
    },
    {
      name: 'atrlen',
      displayName: 'ATR Period',
      type: 'int',
      default: 10,
      min: 1,
      max: 100,
    },
  ],
}

// Donchian Channel - requires high, low
const donchianChannelSchema: UniversalNodeSchema = {
  type: 'donchianchannel',
  displayName: 'Donchian Channel',
  aliases: ['DC'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'upper',
      displayName: 'Upper Channel',
      type: 'number',
    },
    {
      id: 'middle',
      displayName: 'Middle Channel',
      type: 'number',
    },
    {
      id: 'lower',
      displayName: 'Lower Channel',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 100,
    },
  ],
}

// Aroon Indicator - requires high, low
const aroonSchema: UniversalNodeSchema = {
  type: 'aroonindicator',
  face: { shortName: 'Aroon' },
  displayName: 'Aroon Indicator',
  aliases: ['Aroon'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'up',
      displayName: 'Aroon Up',
      type: 'number',
    },
    {
      id: 'down',
      displayName: 'Aroon Down',
      type: 'number',
    },
    {
      id: 'oscillator',
      displayName: 'Aroon Oscillator',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
    },
  ],
}

// TRIX
const trixSchema: UniversalNodeSchema = {
  type: 'trix',
  displayName: 'TRIX',
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput({ withFieldSelection: true })],
  outputs: [
    {
      id: 'trix',
      displayName: 'TRIX',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 18,
      min: 1,
      max: 100,
    },
  ],
}

// Volume Weighted Average Price (VWAP) - requires high, low, close, volume
const vwapSchema: UniversalNodeSchema = {
  type: 'vwap',
  displayName: 'VWAP',
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'vwap',
      displayName: 'VWAP',
      type: 'number',
    },
  ],
  parameters: [], // VWAP has no parameters
}

// On Balance Volume (OBV) - requires close, volume
const obvSchema: UniversalNodeSchema = {
  type: 'obv',
  displayName: 'On Balance Volume',
  aliases: ['OBV'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'obv',
      displayName: 'OBV',
      type: 'number',
    },
  ],
  parameters: [], // OBV has no parameters
}

// Accumulation/Distribution Index (ADI) - requires high, low, close, volume
const adiSchema: UniversalNodeSchema = {
  type: 'adi',
  displayName: 'Accum/Distribution Index',
  aliases: ['ADI', 'A/D', 'AccDist'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'adi',
      displayName: 'A/D Index',
      type: 'number',
    },
  ],
  parameters: [], // ADI has no parameters - uses full OHLCV data
}

// Slope - Linear regression slope indicator
const slopeSchema: UniversalNodeSchema = {
  category: 'indicators',
  type: 'slope',
  displayName: 'Slope',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: 'Data',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Slope',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
    },
  ],
}

// Money Flow Index (MFI) - requires high, low, close, volume
const mfiSchema: UniversalNodeSchema = {
  type: 'mfi',
  displayName: 'Money Flow Index',
  aliases: ['MFI'],
  category: 'indicators',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [createOHLCVInput()],
  outputs: [
    {
      id: 'mfi',
      displayName: 'MFI',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
    },
  ],
}

/**
 * Registration function to add all technical indicator schemas to the universal registry
 */
export function registerTechnicalSchemas(): void {
  // Register all technical indicator schemas
  UniversalNodeRegistry.register(smaSchema)
  UniversalNodeRegistry.register(emaSchema)
  UniversalNodeRegistry.register(wmaSchema)
  UniversalNodeRegistry.register(rsiSchema)
  UniversalNodeRegistry.register(macdSchema)
  UniversalNodeRegistry.register(bollingerBandsSchema)
  UniversalNodeRegistry.register(atrSchema)
  UniversalNodeRegistry.register(adxSchema)
  UniversalNodeRegistry.register(stochasticSchema)
  UniversalNodeRegistry.register(stochasticRsiSchema)
  UniversalNodeRegistry.register(williamsRSchema)
  UniversalNodeRegistry.register(rocSchema)
  UniversalNodeRegistry.register(awesomeOscillatorSchema)
  UniversalNodeRegistry.register(kstSchema)
  UniversalNodeRegistry.register(parabolicSarSchema)
  UniversalNodeRegistry.register(keltnerChannelSchema)
  UniversalNodeRegistry.register(donchianChannelSchema)
  UniversalNodeRegistry.register(aroonSchema)
  UniversalNodeRegistry.register(trixSchema)
  UniversalNodeRegistry.register(vwapSchema)
  UniversalNodeRegistry.register(obvSchema)
  UniversalNodeRegistry.register(adiSchema)
  UniversalNodeRegistry.register(mfiSchema)
  UniversalNodeRegistry.register(slopeSchema)

  // console.log('Technical indicator schemas registered:', UniversalNodeRegistry.getAll().length, 'total schemas');
}
